import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useLocation as useGeoLocation } from "../lib/location";
import {
  Star, MapPin, Truck, Package, Car, CarTaxiFront, Wrench, CircleDot,
  Loader2, ChevronLeft, Clock, Navigation, Sparkles, Construction, Droplets, Bus,
} from "lucide-react";

// Meta table for the horizontal category shelf at the top of the page.
// Ordered by "everyday need" first, urgent/luxury later.
const CATEGORIES = [
  { id: "taxi",      icon: CarTaxiFront },
  { id: "delivery",  icon: Package },
  { id: "moving",    icon: Truck },
  { id: "towing",    icon: Car },
  { id: "mechanic",  icon: Wrench },
  { id: "tire",      icon: CircleDot },
  { id: "carwash",   icon: Droplets },
  { id: "bus",       icon: Bus },
  { id: "luxury",    icon: Sparkles },
  { id: "crane",     icon: Construction },
];
const META = CATEGORIES.reduce((acc, c) => { acc[c.id] = c; return acc; }, {});

export default function Services() {
  const { category } = useParams();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { loc, request } = useGeoLocation();
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setItems(null);
    const params = {
      ...(category ? { category } : {}),
      ...(loc ? { lat: loc.lat, lng: loc.lng } : {}),
    };
    api.get("/listings", { params })
      // Defensive: `/listings` returns an array in production, but a WKWebView
      // first-launch fetch has been observed to receive a non-array body
      // (proxy interstitial / edge-cached wrapper) on iOS. The old
      // `setItems(r.data)` blindly stored whatever came back, and the
      // downstream `items && items.map(...)` render then crashed with
      // `TypeError: l.map is not a function`. Coercing to `[]` at the
      // setter is a no-op when the payload is a proper array (Safari /
      // Web behaviour is byte-identical) and turns the pathological path
      // into an empty-state render instead of an app-wide startup crash.
      .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
      .catch((e) => setError(e.message));
  }, [category, loc]);

  const isCustomer = user?.role === "customer";
  const bookHref = (l) => (isCustomer ? `/request?category=${l.category}` : `/book/${l.listing_id}`);

  return (
    <div className="max-w-[720px] mx-auto pb-6">
      {/* Header — a single clean title, no "// ALL SERVICES" eyebrow. */}
      <div className="px-4 pt-5 pb-3 flex items-end justify-between gap-3">
        <div className="min-w-0">
          <button
            onClick={() => nav(-1)}
            className="w-9 h-9 -ms-1 mb-2 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center rtl-flip"
            data-testid="back-btn"
          >
            <ChevronLeft size={18} />
          </button>
          <h1 className="text-3xl font-black tracking-tight leading-none">
            {category ? t(`categories.${category}`) : t("services.title")}
          </h1>
          <div className="mt-1.5 text-sm text-white/50">
            {category
              ? t("services.categorySub", "Nearby providers ready to help")
              : t("services.sub", "Book any service on-demand")}
          </div>
        </div>
      </div>

      {/* Location chip — subtle, no giant border */}
      <div className="px-4 pb-4">
        <button
          onClick={request}
          className="flex items-center gap-2.5 rounded-full bg-white/5 hover:bg-white/[0.08] border border-white/10 px-3.5 py-2 text-[13px] transition-colors"
          data-testid="services-location-btn"
        >
          <Navigation size={13} className="text-brand-accent" />
          <span className="truncate max-w-[240px]">
            {loc?.label || (loc ? `${loc.lat.toFixed(3)}, ${loc.lng.toFixed(3)}` : t("landing.setLocation"))}
          </span>
          {loc && (
            <span className="text-[10px] text-brand-accent font-semibold ms-1">
              · {t("services.sortedNearest", "Sorted by nearest")}
            </span>
          )}
        </button>
      </div>

      {/* Category shelf — horizontal scroll, Uber-style pills */}
      <div className="pb-4 -mx-4 px-4 no-scrollbar overflow-x-auto">
        <div className="flex gap-2 px-4 min-w-max" data-testid="category-filter">
          <Link
            to="/services"
            data-testid="filter-all"
            className={`shrink-0 rounded-full px-4 py-2 text-[13px] font-semibold transition-colors ${
              !category
                ? "bg-white text-black"
                : "bg-white/5 text-white/80 hover:bg-white/10 border border-white/10"
            }`}
          >
            {t("services.all")}
          </Link>
          {CATEGORIES.map((c) => (
            <Link
              key={c.id}
              to={`/services/${c.id}`}
              data-testid={`filter-${c.id}`}
              className={`shrink-0 rounded-full inline-flex items-center gap-1.5 px-4 py-2 text-[13px] font-semibold transition-colors ${
                category === c.id
                  ? "bg-white text-black"
                  : "bg-white/5 text-white/80 hover:bg-white/10 border border-white/10"
              }`}
            >
              <c.icon size={14} strokeWidth={2.2} />
              {t(`categories.${c.id}`)}
            </Link>
          ))}
        </div>
      </div>

      {/* Results */}
      <section className="px-4 space-y-3 min-h-[240px]">
        {items === null && !error && (
          <div className="flex items-center gap-2 text-xs text-white/50 justify-center py-16">
            <Loader2 className="animate-spin" size={14} /> {t("services.loading")}
          </div>
        )}
        {error && (
          <div className="rounded-2xl bg-red-500/10 border border-red-500/30 px-4 py-3 text-sm text-red-300">
            {error}
          </div>
        )}
        {items && items.length === 0 && (
          <div className="text-center py-16">
            <div className="text-lg font-bold">{t("services.empty")}</div>
            <div className="text-sm text-white/50 mt-1">{t("services.emptyDesc")}</div>
          </div>
        )}
        {items && items.map((l) => (
          <ListingCard key={l.listing_id} l={l} href={bookHref(l)} />
        ))}
      </section>
    </div>
  );
}

/**
 * A single listing row — Uber-style: image on the left, tight typography,
 * subtle border, hover lift.  No visual competition between title / price /
 * rating; each has one spot in the hierarchy.
 */
function ListingCard({ l, href }) {
  const { t } = useTranslation();
  const cat = t(`categories.${l.category}`);
  const rating = l.supplier_rating?.toFixed(1);
  return (
    <Link
      to={href}
      data-testid={`listing-${l.listing_id}`}
      className="group relative flex gap-3 rounded-2xl bg-white/[0.03] hover:bg-white/[0.06] border border-white/10 hover:border-white/20 overflow-hidden transition-all"
    >
      <div className="w-24 sm:w-28 aspect-square shrink-0 bg-white/[0.04] relative">
        {l.image_url ? (
          <img src={l.image_url} alt="" loading="lazy" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/25">
            {(() => {
              const Icon = META[l.category]?.icon || Package;
              return <Icon size={26} strokeWidth={1.6} />;
            })()}
          </div>
        )}
      </div>
      <div className="flex-1 py-3 pe-4 min-w-0 flex flex-col justify-between">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-wider text-white/50 font-semibold">{cat}</div>
          <div className="mt-0.5 text-[15px] font-bold leading-snug line-clamp-2">{l.title}</div>
        </div>
        <div className="flex items-center justify-between gap-3 mt-2 text-[12px] text-white/60">
          <div className="flex items-center gap-3">
            {rating && (
              <span className="inline-flex items-center gap-1">
                <Star size={11} className="text-brand-warn" fill="currentColor" />
                <span className="text-white/80 font-semibold">{rating}</span>
              </span>
            )}
            <span className="inline-flex items-center gap-1">
              <Clock size={11} /> {l.eta_minutes || 30}m
            </span>
            {l.distance_km !== undefined && l.distance_km !== null && (
              <span className="inline-flex items-center gap-1">
                <MapPin size={11} /> {l.distance_km} km
              </span>
            )}
          </div>
          <div className="text-white font-bold text-base whitespace-nowrap">
            ${l.price}
            <span className="ms-1 text-[10px] font-semibold text-white/50 uppercase">
              / {l.unit}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}
